import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Modal, Typography } from '@mui/material';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SearchIcon from '@mui/icons-material/Search';
import { axiosInstance } from '../../../utils/tokenUtils';
import { formatDateFull } from '../../../utils/dateUtils';
import './SessionRecordings.css';

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined) return null;
  const total = Math.round(Number(seconds));
  if (!Number.isFinite(total) || total < 0) return null;

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes) return `${minutes}m ${String(secs).padStart(2, '0')}s`;
  return `${secs}s`;
};

const formatFileSize = bytes => {
  if (bytes === null || bytes === undefined) return null;
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return null;

  const mb = size / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

// Playhead-style stamp (0:07, 1:02:33) -- these are offsets into the recording,
// so they have to line up with what the video control shows.
const formatTimestamp = seconds => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
};

const buildMetaLine = recording =>
  [
    recording.starting_time ? formatDateFull(recording.starting_time) : null,
    formatDuration(recording.duration_seconds),
    formatFileSize(recording.file_size),
  ]
    .filter(Boolean)
    .join(' • ');

const getSegments = recording => (Array.isArray(recording?.transcript) ? recording.transcript : []);

// What to say on the card before anyone opens the player, so nobody clicks in
// hoping for a transcript that was never generated.
const describeTranscript = recording => {
  const status = recording?.transcript_status;
  const count = getSegments(recording).length;

  if (status === 'ready' && count) {
    return { text: `Transcript · ${count} ${count === 1 ? 'line' : 'lines'}`, tone: 'ok' };
  }
  if (status === 'processing') {
    return { text: 'Transcript processing', tone: 'pending' };
  }
  if (status === 'empty') {
    return { text: 'No speech detected', tone: 'muted' };
  }
  return { text: 'No transcript', tone: 'muted' };
};

const findActiveSegment = (segments, time) => {
  let active = -1;
  for (let i = 0; i < segments.length; i += 1) {
    const start = Number(segments[i].start_seconds);
    if (!Number.isFinite(start) || start > time) break;
    active = i;
  }
  return active;
};

/**
 * Session recordings for a booking, read from the admin booking-recordings API.
 *
 * The links the API hands back are minted per request and expire in about a day,
 * so nothing is cached: they are fetched when the view that needs them opens and
 * re-fetched on demand when a link turns out to be dead.
 *
 * `variant="section"` (default) fetches on mount -- that is the spinner on a
 * detail page -- and renders a fragment of list items plus its own player modal,
 * so the page keeps its own section wrapper and can place other items (document,
 * chat) alongside.
 *
 * `variant="cell"` is for table rows: it fetches only when the admin asks, since
 * every call costs an upstream Pencil round trip per recording and a page of rows
 * would otherwise fan out into dozens of them on load.
 */
const SessionRecordings = ({ bookingId, emptyLabel, variant }) => {
  const isCell = variant === 'cell';

  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(!isCell && Boolean(bookingId));
  const [error, setError] = useState(null);
  const [requested, setRequested] = useState(!isCell);
  const [openPlayer, setOpenPlayer] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [playbackError, setPlaybackError] = useState(false);
  const [query, setQuery] = useState('');
  const [activeSegment, setActiveSegment] = useState(-1);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [summaryOverflows, setSummaryOverflows] = useState(false);

  const videoRef = useRef(null);
  const transcriptListRef = useRef(null);
  const summaryTextRef = useRef(null);
  // Mirrors `activeSegment` so the timeupdate handler (~4x/second) can bail out
  // without a state write, instead of re-rendering the whole transcript.
  const activeSegmentRef = useRef(-1);

  const fetchRecordings = useCallback(async () => {
    if (!bookingId) {
      setRecordings([]);
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const { data } = await axiosInstance.get(`/admin/sessions/id/${bookingId}/recordings`);
      const list = Array.isArray(data) ? data : [];
      setRecordings(list);
      return list;
    } catch (err) {
      console.error('Error fetching session recordings:', err);
      setRecordings([]);
      setError(
        err?.response?.status === 403
          ? "You don't have permission to view session recordings"
          : 'Failed to load session recordings'
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (isCell) return;
    fetchRecordings();
  }, [isCell, fetchRecordings]);

  const resetPlayback = () => {
    setPlaybackError(false);
    setQuery('');
    setActiveSegment(-1);
    activeSegmentRef.current = -1;
    setSummaryExpanded(false);
    setSummaryOverflows(false);
  };

  const handleOpenPlayer = index => {
    setSelectedIndex(index);
    resetPlayback();
    setOpenPlayer(true);
  };

  const handleClosePlayer = () => {
    setOpenPlayer(false);
    resetPlayback();
  };

  // A dead link means the one Pencil issued has expired; asking again mints a new one.
  const handleRefreshLinks = async () => {
    setPlaybackError(false);
    await fetchRecordings();
  };

  // Cell variant only: pull the recordings on demand, then play the first straight away.
  const handleRequestRecordings = async event => {
    event?.stopPropagation();
    setRequested(true);
    const list = await fetchRecordings();
    if (list.length) handleOpenPlayer(0);
  };

  const selectedRecording = recordings[selectedIndex];
  const segments = getSegments(selectedRecording);
  const summaryText = selectedRecording?.transcript_summary;

  const visibleSegments = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const indexed = segments.map((segment, index) => ({ segment, index }));
    if (!needle) return indexed;
    return indexed.filter(({ segment }) => (segment.text || '').toLowerCase().includes(needle));
  }, [segments, query]);

  const handleTimeUpdate = () => {
    const time = videoRef.current?.currentTime;
    if (!Number.isFinite(time)) return;

    const next = findActiveSegment(segments, time);
    if (next !== activeSegmentRef.current) {
      activeSegmentRef.current = next;
      setActiveSegment(next);
    }
  };

  // Follow along while the video plays -- but not while a search is narrowing the
  // list, or the results would jump out from under the pointer.
  useEffect(() => {
    if (query || activeSegment < 0 || !transcriptListRef.current) return;
    const row = transcriptListRef.current.querySelector(`[data-segment="${activeSegment}"]`);
    if (row) row.scrollIntoView({ block: 'nearest' });
  }, [activeSegment, query]);

  // Offer "Show more" only when the summary is actually being cut off. Measured
  // rather than guessed from length, because how many lines a summary takes
  // depends on the modal width. Only meaningful while collapsed -- expanded, the
  // clamp is off and nothing overflows.
  useEffect(() => {
    if (!openPlayer || summaryExpanded) return undefined;

    const measure = () => {
      const el = summaryTextRef.current;
      if (el) setSummaryOverflows(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [openPlayer, summaryExpanded, summaryText, selectedIndex]);

  const handleSeek = startSeconds => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(Number(startSeconds))) return;

    video.currentTime = Number(startSeconds);
    const played = video.play();
    // Autoplay can be refused; the seek still lands, so ignore the rejection.
    if (played?.catch) played.catch(() => {});
  };

  const renderTranscript = () => {
    const status = selectedRecording?.transcript_status;

    if (status === 'processing') {
      return (
        <Box className="session-recording-transcript-empty">
          <span className="session-recording-meta">
            Pencil is still transcribing this recording.
          </span>
          <Button size="small" variant="outlined" onClick={fetchRecordings} disabled={loading}>
            {loading ? 'Checking...' : 'Check again'}
          </Button>
        </Box>
      );
    }

    if (!segments.length) {
      return (
        <Box className="session-recording-transcript-empty">
          <span className="session-recording-meta">
            {status === 'empty'
              ? 'No speech was picked up in this recording.'
              : 'No transcript is available for this recording.'}
          </span>
        </Box>
      );
    }

    return (
      <>
        <Box className="session-recording-transcript-search">
          <SearchIcon sx={{ color: '#949DA6', fontSize: '18px' }} />
          <input
            type="text"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search transcript"
            aria-label="Search transcript"
          />
          {query && (
            <Button
              size="small"
              className="session-recording-clear-btn"
              onClick={() => setQuery('')}
            >
              Clear
            </Button>
          )}
        </Box>

        <Box className="session-recording-transcript-list" ref={transcriptListRef}>
          {visibleSegments.length === 0 ? (
            <span className="session-recording-meta">No lines match “{query}”.</span>
          ) : (
            visibleSegments.map(({ segment, index }) => {
              const previous = index > 0 ? segments[index - 1] : null;
              // Repeating the name on every line of the same turn is noise. Keyed on
              // the id, not the name: two people can share a display name, and the
              // label would then silently merge their turns.
              const showSpeaker = !previous || previous.speaker_id !== segment.speaker_id;

              return (
                <Box
                  key={index}
                  data-segment={index}
                  className={`session-recording-segment${
                    index === activeSegment ? ' session-recording-segment--active' : ''
                  }`}
                  onClick={() => handleSeek(segment.start_seconds)}
                >
                  <span className="session-recording-segment-time">
                    {formatTimestamp(segment.start_seconds)}
                  </span>
                  <Box className="session-recording-segment-body">
                    {showSpeaker && (
                      <span className="session-recording-segment-speaker">
                        {segment.speaker_name || 'Unknown speaker'}
                      </span>
                    )}
                    <span className="session-recording-segment-text">{segment.text}</span>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </>
    );
  };

  const renderSection = () => {
    // Just a spinner, sized to the card: the recordings call is the only thing
    // pending here, so it must not read as the page still loading.
    if (loading) {
      return (
        <Box className="session-recording-loader">
          <CircularProgress size={22} sx={{ color: '#0A5EF3' }} />
        </Box>
      );
    }

    if (error) {
      return (
        <div className="session-recording-item session-recording-item--static">
          <span className="session-recording-text session-recording-text--error">{error}</span>
          <Button size="small" onClick={fetchRecordings} className="session-recording-retry-btn">
            Retry
          </Button>
        </div>
      );
    }

    if (!recordings.length) {
      return (
        <div className="session-recording-item session-recording-item--static">
          <span className="session-recording-text">{emptyLabel}</span>
        </div>
      );
    }

    return recordings.map((recording, index) => {
      const meta = buildMetaLine(recording);
      const transcript = describeTranscript(recording);

      return (
        <div
          key={recording.recording_id || index}
          className="session-recording-item session-recording-item--clickable"
          onClick={() => handleOpenPlayer(index)}
        >
          <Box className="session-recording-icon">
            <VideoLibraryIcon sx={{ color: '#0A5EF3' }} />
          </Box>
          <Box className="session-recording-details">
            <span className="session-recording-text">
              Session Recording {recordings.length > 1 ? `(${index + 1})` : ''}
            </span>
            {meta && <span className="session-recording-meta">{meta}</span>}
            <span className={`session-recording-badge session-recording-badge--${transcript.tone}`}>
              {transcript.text}
            </span>
          </Box>
        </div>
      );
    });
  };

  const renderCell = () => {
    if (loading) {
      return (
        <Box className="session-recording-cell">
          <CircularProgress size={16} sx={{ color: '#0A5EF3' }} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box className="session-recording-cell">
          <span className="session-recording-cell-text session-recording-text--error">
            Couldn't load
          </span>
          <Button
            size="small"
            className="session-recording-retry-btn"
            onClick={handleRequestRecordings}
          >
            Retry
          </Button>
        </Box>
      );
    }

    if (!requested) {
      return (
        <Button
          size="small"
          startIcon={<VideoLibraryIcon sx={{ fontSize: '18px' }} />}
          className="session-recording-view-btn"
          onClick={handleRequestRecordings}
          disabled={!bookingId}
        >
          View recording
        </Button>
      );
    }

    if (!recordings.length) {
      return <span className="session-recording-cell-text">No recording</span>;
    }

    return (
      <Box className="session-recording-cell session-recording-cell--stacked">
        {recordings.map((recording, index) => (
          <Box
            key={recording.recording_id || index}
            className="session-recording-chip"
            onClick={event => {
              event.stopPropagation();
              handleOpenPlayer(index);
            }}
          >
            <VideoLibraryIcon sx={{ color: '#0A5EF3', fontSize: '18px' }} />
            <span className="session-recording-cell-text">
              Recording {recordings.length > 1 ? `(${index + 1})` : ''}
            </span>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      {isCell ? renderCell() : renderSection()}

      <Modal
        open={openPlayer}
        onClose={handleClosePlayer}
        aria-labelledby="session-recording-player-title"
        aria-describedby="session-recording-player"
      >
        <Box className="session-recording-modal">
          <Box className="session-recording-modal-header">
            <Typography id="session-recording-player-title" variant="h6" component="h2">
              Session Recording {recordings.length > 1 ? `(${selectedIndex + 1})` : ''}
            </Typography>
            <Button onClick={handleClosePlayer} className="session-recording-close-btn">
              ✕
            </Button>
          </Box>

          <Box className="session-recording-modal-body">
            <Box className="session-recording-video-col">
              <Box className="session-recording-player-frame">
                {playbackError ? (
                  <Box className="session-recording-playback-error">
                    <span className="session-recording-text session-recording-text--error">
                      This recording link has expired or could not be played.
                    </span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleRefreshLinks}
                      disabled={loading}
                    >
                      {loading ? 'Refreshing...' : 'Refresh link'}
                    </Button>
                  </Box>
                ) : (
                  selectedRecording?.download_url && (
                    <video
                      key={selectedRecording.download_url}
                      ref={videoRef}
                      className="session-recording-video"
                      src={selectedRecording.download_url}
                      onError={() => setPlaybackError(true)}
                      onTimeUpdate={handleTimeUpdate}
                      controls
                      autoPlay
                    />
                  )
                )}
              </Box>

              {summaryText && (
                <Box className="session-recording-summary">
                  <span className="session-recording-summary-label">
                    <span aria-hidden="true">✨</span> AI Summary
                  </span>
                  <span
                    ref={summaryTextRef}
                    className={`session-recording-meta session-recording-summary-text${
                      summaryExpanded ? ' session-recording-summary-text--expanded' : ''
                    }`}
                  >
                    {summaryText}
                  </span>
                  {(summaryOverflows || summaryExpanded) && (
                    <Button
                      size="small"
                      className="session-recording-more-btn"
                      onClick={() => setSummaryExpanded(previous => !previous)}
                    >
                      {summaryExpanded ? 'Show less' : 'Show more'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            <Box className="session-recording-transcript-col">
              <Box className="session-recording-transcript-header">
                <span className="session-recording-transcript-title">Transcript</span>
                {segments.length > 0 && (
                  <span className="session-recording-meta">
                    {segments.length} {segments.length === 1 ? 'line' : 'lines'}
                  </span>
                )}
              </Box>
              {renderTranscript()}
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

SessionRecordings.propTypes = {
  bookingId: PropTypes.string,
  emptyLabel: PropTypes.string,
  variant: PropTypes.oneOf(['section', 'cell']),
};

SessionRecordings.defaultProps = {
  bookingId: null,
  emptyLabel: 'No recording available',
  variant: 'section',
};

export default SessionRecordings;
